import { API_ROUTES, ESERVICES_PRIVACY_POLICY_TYPE } from "@/constant";
import AuthAPI from "@/services/Auth/AuthConfig";
import {
  AlertResponseInterface,
  BranchPaymentConfigResponse,
  ChangePasswordPayloadInterface,
  CheckDowntimeResponse,
  DashboardData,
  EmailVerifyPayloadInterface,
  EmailVerifyResponseInterface,
  ForgotPasswordPayloadInterface,
  GetCustomerResponse,
  LoginOtpPayloadInterface,
  LoginPayloadInterface,
  LoginResponse,
  LoginResponseInterface,
  NewOtpPayloadInterface,
  OccupationListResponse,
  OtpPayloadInterface,
  OtpVerifyPayloadInterface,
  PromotionListResponseInterface,
  ResendOtpPayload,
  ResendOtpPayloadInterface,
  ResetPasswordNewPayloadInterface,
  ResetPasswordPayloadInterface,
  TenantMasterDataResponseInterface,
  UploadFilePayloadInterface,
  UploadFileResponseInterface,
  VerifyOtpResponseInterface,
} from "@/types/authService";

export const loginUser = async (payload: LoginPayloadInterface): Promise<any> => {
  const response = await AuthAPI.post<LoginResponseInterface>(API_ROUTES.login, payload);
  return response;
};

export const loginUserMFA = async (payload: LoginPayloadInterface): Promise<any> => {
  const response = await AuthAPI.post<LoginResponseInterface>(API_ROUTES.login, payload);

  return response.data;
};

export const customerVerifyOTP = async (payload: OtpVerifyPayloadInterface): Promise<any> => {
  const response = await AuthAPI.post<VerifyOtpResponseInterface>(API_ROUTES.verifyOtp, payload);

  return response.data;
};

export const customerResendOTP = async (payload: ResendOtpPayload): Promise<any> => {
  const response = await AuthAPI.post<any>(API_ROUTES.resendLoginOtp, payload);
  return response;
};

export const SingpassLogin = async (code: string, state: string): Promise<any> => {
  try {
    const response = await AuthAPI.get(API_ROUTES.singpassValidate, {
      params: { code, state },
    });
    return response.data;
  } catch (error) {
    console.error("SingPass validation failed:", error);
    throw error;
  }
};

export const submitLoginOtp = async (payload: LoginOtpPayloadInterface): Promise<LoginResponse> => {
  const response = await AuthAPI.post<LoginResponse>(API_ROUTES.loginOtpVerify, payload);
  return response.data;
};

export const resendLoginOtp = async (payload: ResendOtpPayloadInterface): Promise<void> => {
  await AuthAPI.post<void>(API_ROUTES.loginOtpRetry, payload);
};

export const forgotPassword = async (payload: ForgotPasswordPayloadInterface): Promise<any> => {
  const response = await AuthAPI.post<any>(API_ROUTES.forgotPassword, payload);
  return response.data;
};

export const resetPassword = async (payload: ResetPasswordPayloadInterface): Promise<void> => {
  await AuthAPI.put<void>(API_ROUTES.resetPassword, payload);
};

export const changePassword = async (payload: ChangePasswordPayloadInterface): Promise<void> => {
  await AuthAPI.put<void>(API_ROUTES.changePassword, payload);
};

export const submitOtp = async (payload: OtpPayloadInterface): Promise<void> => {
  await AuthAPI.put<LoginResponse>(API_ROUTES.otpVerify, payload);
};

export const resendOtp = async (): Promise<void> => {
  await AuthAPI.put<void>(API_ROUTES.otpRetry);
};

export const getDashboardData = async (params?: any): Promise<DashboardData> => {
  const response = await AuthAPI.get<DashboardData>(API_ROUTES.customerDashboard, { params });
  return response.data;
};

export const getUserData = async (): Promise<GetCustomerResponse> => {
  const response = await AuthAPI.get<GetCustomerResponse>(API_ROUTES.customerData);
  return response.data;
};

export const updateCustomer = async (id: string, payload: any): Promise<any> => {
  const response = await AuthAPI.put<any>(`${API_ROUTES.customers}/${id}`, payload);
  return response.data;
};

export const customerVerifyEmail = async (payload: EmailVerifyPayloadInterface): Promise<any> => {
  const response = await AuthAPI.post<EmailVerifyResponseInterface>(
    API_ROUTES.customersVerifyEmail,
    payload,
  );
  return response;
};

export const acceptTermsConditions = async (payload: any): Promise<void> => {
  await AuthAPI.post<void>(API_ROUTES.acceptTermsConditions, payload);
};

export const resetPasswordNew = async (
  payload: ResetPasswordNewPayloadInterface,
): Promise<void> => {
  await AuthAPI.post<void>(API_ROUTES.resetPasswordNew, payload);
};

export const submitOtpNew = async (payload: NewOtpPayloadInterface): Promise<any> => {
  const response = await AuthAPI.post<LoginResponse>(API_ROUTES.forgotVerifyOTP, payload);
  return response;
};

export const showAlert = async (queryString?: string): Promise<AlertResponseInterface> => {
  const response = await AuthAPI.get<AlertResponseInterface>(
    `${API_ROUTES.show_alert}${queryString ? `?${queryString}` : ""}`,
  );
  return response.data;
};

export const fileUpload = async (
  payload: UploadFilePayloadInterface,
): Promise<UploadFileResponseInterface> => {
  const formData = new FormData();
  formData.append("file", payload.file);
  formData.append("type", payload.type);

  const response = await AuthAPI.post<UploadFileResponseInterface>(
    API_ROUTES.FILE_UPLOAD,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return response.data;
};

export const getPromotions = async (
  queryString?: string,
): Promise<PromotionListResponseInterface> => {
  const response = await AuthAPI.get<PromotionListResponseInterface>(
    `${API_ROUTES.PROMOTIONS}${queryString ? `?${queryString}` : ""}`,
  );
  return response.data;
};

export const getTenantMasterData = async (
  referenceId?: string,
): Promise<TenantMasterDataResponseInterface> => {
  const response = await AuthAPI.get<TenantMasterDataResponseInterface>(
    `${API_ROUTES.TENANT_MASTER_DATA}/${referenceId}`,
  );
  return response.data;
};

export const getTenantMasterPledgeData = async (queryString?: any): Promise<any> => {
  const response = await AuthAPI.get<any>(
    `${API_ROUTES.TENANT_MASTER_PLEDGE_DATA}${queryString ? `?${queryString}` : ""}`,
  );
  return response.data;
};

export const getTenantDowntime = async (): Promise<any> => {
  const response = await AuthAPI.get<any>(API_ROUTES.TENANT_DOWNTIME);
  return response.data;
};

export const getEservicesPrivacyPolicy = async (): Promise<any> => {
  const response = await AuthAPI.get(API_ROUTES.TERMS_AND_CONDITIONS, {
    params: {
      type: ESERVICES_PRIVACY_POLICY_TYPE,
      is_active: true,
      is_deleted: false,
      page: 1,
      limit: 1,
      sort_by: "id",
      sort_order: "DESC",
    },
  });
  return response.data;
};

export const getTermAndConditions = async (queryString?: string): Promise<any> => {
  const response = await AuthAPI.get<any>(
    `${API_ROUTES.TERMS_AND_CONDITIONS}${queryString ? `?${queryString}` : ""}`,
  );
  return response.data;
};

export const getPromotionById = async (id: string): Promise<any> => {
  const response = await AuthAPI.get<any>(`${API_ROUTES.GET_PROMOTION}/${id}`);
  return response.data;
};

export const initialteSingpassSign = async (params?: any): Promise<any> => {
  const response = await AuthAPI.get(API_ROUTES.PLEDGE_SIGN, { params });
  return response.data;
};

export const initiateSingpassSign = async (params?: any): Promise<any> => {
  const response = await AuthAPI.get(API_ROUTES.INITIATE_SINGPASS_SIGN, { params });
  return response.data;
};

export const getOccupationList = async (queryString?: string): Promise<OccupationListResponse> => {
  const response = await AuthAPI.get<OccupationListResponse>(
    `${API_ROUTES.OCCUPATION}${queryString ? `?${queryString}` : ""}`,
  );
  return response.data;
};

export const getIdType = async (params?: any): Promise<any> => {
  const response = await AuthAPI.get(API_ROUTES.GET_IDTYPE_MASTER, { params });
  return response.data;
};

export const getPaymentMethod = async (params: {
  branch_id: number;
}): Promise<BranchPaymentConfigResponse> => {
  const response = await AuthAPI.get(API_ROUTES.PAYMENT_METHOD, {
    params,
  });
  return response.data;
};

export const getLastestTerms = async (): Promise<any> => {
  const response = await AuthAPI.get(API_ROUTES.LATEST_TERMS);
  return response.data;
};

export const getBannerList = async (params?: any): Promise<any> => {
  const response = await AuthAPI.get(API_ROUTES.BANNER_LIST, { params });
  return response.data;
};

export const checkDownTime = async (): Promise<CheckDowntimeResponse> => {
  const response = await AuthAPI.get<CheckDowntimeResponse>(API_ROUTES.CHECK_DOWNTIME);
  return response.data;
};
